/**
 * Lobby screen - waiting room before market opens
 */

import { useEffect, useState, useCallback } from "preact/hooks";
import { route } from "preact-router";
import { useStore } from "../store";
import { useWebSocket } from "../hooks/useWebSocket";
import { api } from "../api/client";
import type { User } from "../api/types";

interface LobbyProps {
  marketId: string;
}

export function Lobby({ marketId }: LobbyProps) {
  const {
    market,
    user,
    users,
    setUsers,
    addUser,
    setMarket,
    setUser,
    currentMarketId,
    currentUserId,
    setSession,
    addToast,
  } = useStore();
  const [loading, setLoading] = useState(true);
  const [opening, setOpening] = useState(false);
  const [error, setError] = useState("");
  const [restoring, setRestoring] = useState(!market || !user);

  // Restore session if needed
  useEffect(() => {
    if (market && user) {
      setRestoring(false);
      return;
    }

    // Try to restore from persisted session
    const storedMarketId = currentMarketId;
    const storedUserId = currentUserId;

    if (storedMarketId === marketId && storedUserId) {
      // Restore session from API
      Promise.all([api.getMarket(marketId), api.getUser(storedUserId)])
        .then(([marketData, userData]) => {
          setMarket(marketData);
          setUser(userData);
          setRestoring(false);
        })
        .catch((err) => {
          console.error("Failed to restore session:", err);
          // Session invalid, redirect to home
          setSession(null, null);
          route("/");
        });
    } else {
      // No valid session for this market, redirect to home
      setRestoring(false);
      route("/");
    }
  }, [
    marketId,
    market,
    user,
    currentMarketId,
    currentUserId,
    setMarket,
    setUser,
    setSession,
  ]);

  // Load leaderboard (users list)
  useEffect(() => {
    api
      .getLeaderboard(marketId)
      .then((data) => {
        setUsers(data.users.map((entry) => entry.user));
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [marketId, setUsers]);

  // WebSocket for real-time updates
  useWebSocket(marketId, user?.id ?? null, {
    onMessage: (msg) => {
      if (msg.type === "user_joined") {
        const data = msg.data as User;
        addUser(data);
      }
      if (msg.type === "market_opened") {
        route(`/market/${marketId}`);
      }
    },
  });

  const handleOpenMarket = async () => {
    if (!user?.isAdmin) return;
    setOpening(true);
    setError("");

    try {
      await api.openMarket(marketId, user.id);
      route(`/market/${marketId}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to open market");
      setOpening(false);
    }
  };

  const copyInviteCode = useCallback(() => {
    if (market?.inviteCode) {
      navigator.clipboard.writeText(market.inviteCode);
      addToast({
        message: "Copied to clipboard!",
        type: "success",
        duration: 2000,
      });
    }
  }, [market?.inviteCode, addToast]);

  if (restoring || !market || !user) {
    return (
      <div class="screen lobby">
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div class="screen lobby">
      <header>
        <button class="btn-back" onClick={() => route("/")}>
          &larr; Leave
        </button>
        <h2>{market.name}</h2>
      </header>

      <div class="invite-section">
        <p>Share this code to invite players:</p>
        <button class="invite-code" onClick={copyInviteCode}>
          {market.inviteCode}
          <span class="copy-hint">tap to copy</span>
        </button>
      </div>

      <div class="players-section">
        <h3>Players ({users.length})</h3>
        {loading ? (
          <p>Loading...</p>
        ) : (
          <ul class="players-list">
            {users.map((u) => (
              <li key={u.id} class="player-item">
                <span class="player-avatar">{u.avatar}</span>
                <span class="player-name">
                  {u.displayName}
                  {u.isAdmin && <span class="admin-badge">Admin</span>}
                </span>
                <span class="player-balance">${u.balance}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {error && <div class="error">{error}</div>}

      {user.isAdmin && (
        <div class="admin-actions">
          <button
            class="btn btn-primary"
            onClick={handleOpenMarket}
            disabled={opening || users.length < 2}
          >
            {opening ? "Opening..." : "Open Market for Betting"}
          </button>
          {users.length < 2 && (
            <p class="hint">Need at least 2 players to start</p>
          )}
        </div>
      )}

      {!user.isAdmin && (
        <div class="waiting-message">
          <p>Waiting for admin to open the market...</p>
        </div>
      )}
    </div>
  );
}
